// Secure Configuration Management
// This file manages sensitive configuration values securely

/**
 * Security Configuration
 * Uses environment variables in production, fallbacks for development
 */
export const SECURITY_CONFIG = {
  // Encryption settings
  ENCRYPTION: {
    // In production, this should come from environment variables
    KEY: process.env.REACT_APP_ENCRYPTION_KEY || 'chirpx-dev-key-change-in-production',
    ALGORITHM: 'AES',
    KEY_SIZE: 256
  },

  // Rate limiting configuration
  RATE_LIMITS: {
    POST_CREATION: {
      maxRequests: parseInt(process.env.REACT_APP_POST_RATE_LIMIT) || 10,
      windowMs: parseInt(process.env.REACT_APP_POST_RATE_WINDOW) || 60000 // 1 minute
    },
    PROFILE_UPDATE: {
      maxRequests: parseInt(process.env.REACT_APP_PROFILE_RATE_LIMIT) || 5,
      windowMs: parseInt(process.env.REACT_APP_PROFILE_RATE_WINDOW) || 300000 // 5 minutes
    },
    WALLET_CONNECT: {
      maxRequests: parseInt(process.env.REACT_APP_WALLET_RATE_LIMIT) || 20,
      windowMs: parseInt(process.env.REACT_APP_WALLET_RATE_WINDOW) || 60000 // 1 minute
    }
  },

  // Content validation
  CONTENT_LIMITS: {
    POST_MAX_LENGTH: parseInt(process.env.REACT_APP_POST_MAX_LENGTH) || 500,
    DISPLAY_NAME_MAX_LENGTH: parseInt(process.env.REACT_APP_DISPLAY_NAME_MAX_LENGTH) || 50,
    BIO_MAX_LENGTH: parseInt(process.env.REACT_APP_BIO_MAX_LENGTH) || 500,
    HASHTAG_MAX_COUNT: parseInt(process.env.REACT_APP_HASHTAG_MAX_COUNT) || 10
  },

  // Wallet security
  WALLET: {
    ALLOWED_PROVIDERS: (process.env.REACT_APP_ALLOWED_WALLET_PROVIDERS || 'phantom,solflare,nightly').split(','),
    CONNECTION_TIMEOUT: parseInt(process.env.REACT_APP_WALLET_TIMEOUT) || 30000,
    SIGNATURE_TIMEOUT: parseInt(process.env.REACT_APP_SIGNATURE_TIMEOUT) || 60000
  },

  // Session management
  SESSION: {
    TIMEOUT: parseInt(process.env.REACT_APP_SESSION_TIMEOUT) || 3600000, // 1 hour
    REFRESH_THRESHOLD: parseInt(process.env.REACT_APP_SESSION_REFRESH) || 300000, // 5 minutes
    MAX_CONCURRENT_SESSIONS: parseInt(process.env.REACT_APP_MAX_SESSIONS) || 3
  }
};

/**
 * Solana Network Configuration
 * Securely manages blockchain connection settings
 */
export const SOLANA_CONFIG = {
  // Network endpoints (use environment variables in production)
  NETWORKS: {
    DEVNET: process.env.REACT_APP_SOLANA_DEVNET_RPC || 'https://api.devnet.solana.com',
    MAINNET: process.env.REACT_APP_SOLANA_MAINNET_RPC || 'https://api.mainnet-beta.solana.com',
    TESTNET: process.env.REACT_APP_SOLANA_TESTNET_RPC || 'https://api.testnet.solana.com'
  },

  // Current network (controlled by environment)
  CURRENT_NETWORK: process.env.REACT_APP_SOLANA_NETWORK || 'DEVNET',

  // Program IDs (should be environment variables in production)
  PROGRAM_IDS: {
    CHIRPX: process.env.REACT_APP_CHIRPX_PROGRAM_ID || '6eYBoAEeB5VXEfSGmkejDfTmmBxeqDyV5QWr19eF9Zst',
    MEMO: 'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr', // Standard Solana memo program
    TOKEN: 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA' // Standard SPL token program
  },

  // Token configuration
  TOKENS: {
    ZNOOZINU: {
      ADDRESS: process.env.REACT_APP_ZNOOZINU_TOKEN || '5cLKdANQXzUPMXgpba19Zwprhmi8yQW6R5D66oTQZ9ZN',
      DECIMALS: 9,
      SYMBOL: 'ZNOOZINU'
    }
  },

  // Transaction settings
  TRANSACTION: {
    CONFIRMATION_COMMITMENT: 'confirmed',
    MAX_RETRIES: parseInt(process.env.REACT_APP_TX_MAX_RETRIES) || 3,
    TIMEOUT: parseInt(process.env.REACT_APP_TX_TIMEOUT) || 60000,
    FEE_PAYER_PERCENTAGE: parseFloat(process.env.REACT_APP_FEE_PERCENTAGE) || 0.0001 // 0.01%
  }
};

/**
 * Application Security Settings
 */
export const APP_SECURITY = {
  // Feature flags for security
  FEATURES: {
    ENABLE_RATE_LIMITING: process.env.REACT_APP_ENABLE_RATE_LIMITING !== 'false',
    ENABLE_INPUT_SANITIZATION: process.env.REACT_APP_ENABLE_INPUT_SANITIZATION !== 'false',
    ENABLE_WALLET_VALIDATION: process.env.REACT_APP_ENABLE_WALLET_VALIDATION !== 'false',
    ENABLE_SECURE_STORAGE: process.env.REACT_APP_ENABLE_SECURE_STORAGE !== 'false',
    ENABLE_DEBUG_LOGS: process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true'
  },

  // Content Security Policy settings
  CSP: {
    REPORT_URI: process.env.REACT_APP_CSP_REPORT_URI || null,
    ENFORCE_HTTPS: process.env.REACT_APP_ENFORCE_HTTPS !== 'false',
    ALLOW_UNSAFE_EVAL: process.env.REACT_APP_ALLOW_UNSAFE_EVAL === 'true' // Only for wallet libraries
  },

  // Error handling
  ERROR_REPORTING: {
    ENABLE_SENTRY: process.env.REACT_APP_ENABLE_SENTRY === 'true',
    SENTRY_DSN: process.env.REACT_APP_SENTRY_DSN || null,
    SANITIZE_ERRORS: process.env.REACT_APP_SANITIZE_ERRORS !== 'false'
  }
};

/**
 * Get current Solana RPC endpoint
 */
export const getCurrentRpcEndpoint = () => {
  const network = SOLANA_CONFIG.CURRENT_NETWORK;
  return SOLANA_CONFIG.NETWORKS[network] || SOLANA_CONFIG.NETWORKS.DEVNET;
};

/**
 * Get program ID by name
 */
export const getProgramId = (programName) => {
  return SOLANA_CONFIG.PROGRAM_IDS[programName.toUpperCase()];
};

/**
 * Validate configuration on app startup
 */
export const validateConfiguration = () => {
  const errors = [];

  // Check required environment variables in production
  if (process.env.NODE_ENV === 'production') {
    const requiredEnvVars = [
      'REACT_APP_ENCRYPTION_KEY',
      'REACT_APP_CHIRPX_PROGRAM_ID',
      'REACT_APP_SOLANA_NETWORK'
    ];

    requiredEnvVars.forEach(envVar => {
      if (!process.env[envVar]) {
        errors.push(`Missing required environment variable: ${envVar}`);
      }
    });
  }

  // Validate network configuration
  if (!SOLANA_CONFIG.NETWORKS[SOLANA_CONFIG.CURRENT_NETWORK]) {
    errors.push(`Invalid network configuration: ${SOLANA_CONFIG.CURRENT_NETWORK}`);
  }

  // Validate rate limits
  Object.entries(SECURITY_CONFIG.RATE_LIMITS).forEach(([key, config]) => {
    if (!config.maxRequests || !config.windowMs) {
      errors.push(`Invalid rate limit configuration for ${key}`);
    }
  });

  if (errors.length > 0) {
    console.error('Configuration validation errors:', errors);
    if (process.env.NODE_ENV === 'production') {
      throw new Error('Configuration validation failed: ' + errors.join(', '));
    }
  }

  return { valid: errors.length === 0, errors };
};

/**
 * Get safe configuration for client-side logging
 * Removes sensitive information
 */
export const getSafeConfig = () => {
  return {
    network: SOLANA_CONFIG.CURRENT_NETWORK,
    features: APP_SECURITY.FEATURES,
    limits: SECURITY_CONFIG.CONTENT_LIMITS,
    // Exclude sensitive data like keys, tokens, etc.
  };
};

// Validate configuration on module load
if (typeof window !== 'undefined') {
  try {
    validateConfiguration();
    console.log('✅ Security configuration validated successfully');
  } catch (error) {
    console.error('❌ Security configuration validation failed:', error);
  }
}

export default {
  SECURITY_CONFIG,
  SOLANA_CONFIG,
  APP_SECURITY,
  getCurrentRpcEndpoint,
  getProgramId,
  validateConfiguration,
  getSafeConfig
};
